// The board on a phone: column tabs above a scroll-snap scroller that holds one
// full-width column at a time. Tap a tab or swipe sideways to switch. All the
// columns stay mounted, so each keeps its own vertical scroll position and its
// data; the scroller only decides which one is in view.

import { useLayoutEffect, type ReactNode } from 'react';
import { Box } from '@mui/material';
import { ColumnTabs, panelDomId, tabDomId } from './ColumnTabs';
import { useColumnPager } from './useColumnPager';

export interface PhoneColumn {
    id: string;
    title: string;
    node: ReactNode;
}

export interface PhoneBoardProps {
    columns: PhoneColumn[];
}

// The columns beside the one in view stay mounted, but nothing in them may be
// reached: Tab would land in a column the reader cannot see (and focusing it
// scrolls the pager away), and a screen reader would read it as part of the
// page. `inert` takes the whole subtree out of the tab order, the pointer's
// reach and the accessibility tree. React 18 knows no `inert` prop, so it goes
// in as the attribute itself: an empty string is "on", no attribute is "off".
// React 19 makes `inert` a boolean prop and renders the string "" as off, so
// this must become `inert: true` on the upgrade (the hasAttribute tests in
// PhoneBoard.test.tsx fail until it does).
// It cannot stop a swipe: the pointer falls through an inert column to the
// scroller, which is what scrolls.
const INERT = { inert: '' } as Record<string, unknown>;

export function PhoneBoard({ columns }: PhoneBoardProps) {
    const pager = useColumnPager(columns.map(c => c.id));
    const activeId = columns[pager.active]?.id;

    // Focus inside the column the reader just left (a screen reader's
    // three-finger swipe, a trackpad swipe, a tab tap that does not focus) would
    // fall to <body> when that column turns inert. Hand it to the tab of the
    // column now in view, which is where the reader is. Before the browser's own
    // fix-up, which runs after the commit.
    useLayoutEffect(() => {
        const focused = document.activeElement;
        const scroller = pager.scrollerProps.ref.current;
        if (!focused || !scroller?.contains(focused)) return;
        if (!focused.closest('[inert]')) return;
        if (activeId !== undefined)
            document.getElementById(tabDomId(activeId))?.focus();
    }, [activeId, pager.scrollerProps.ref]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
            }}
        >
            <ColumnTabs
                tabs={columns}
                active={pager.active}
                onSelect={pager.select}
            />
            <Box
                {...pager.scrollerProps}
                data-testid="board-pager"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollSnapType: 'x mandatory',
                    // No scroll chaining at the ends: a swipe past the last
                    // column must not navigate the browser's history.
                    overscrollBehaviorX: 'contain',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {columns.map((column, index) => (
                    <Box
                        key={column.id}
                        {...(index === pager.active ? null : INERT)}
                        role="tabpanel"
                        id={panelDomId(column.id)}
                        aria-labelledby={tabDomId(column.id)}
                        sx={{
                            flex: '0 0 100%',
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            scrollSnapAlign: 'start',
                            // A hard flick must stop on the next column, not
                            // skip over it.
                            scrollSnapStop: 'always',
                        }}
                    >
                        {column.node}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
